import React, {FC, useState, useCallback, useRef, useEffect} from 'react';
import {View, Pressable, TextInput, ActivityIndicator} from 'react-native';
import {
  useMtnRequestOtpMutation,
  useMtnVerifyOtpMutation,
} from '@src/Api/TraceApi/traceApi';
import {MtnAllowLoginResponse} from '@src/Api/TraceApi/endpoints/mtn.endpoint';
import {IDeviceInfo} from '@src/Api/TraceApi/endpoints/sessions.endpoint';
import Typography from 'design-system/src/Components/Typography/Typography';
import Metrics from 'design-system/src/Theme/Metrics';
import Icon from 'design-system/src/Components/Icon/Icon';
import appAuthSlice from '@src/Store/Slices/AppSlice/auth.slice';
import {useAppDispatch} from '@src/Store/hooks';
import {useTranslation} from 'gatsby-plugin-react-i18next';
import useMtnLoginCompletion from '@src/Hooks/useMtnLoginCompletion';
import {MTN_SUBSCRIBE_URL} from '@src/Configs/mtnConfig';

const OTP_COOLDOWN_MS = 30_000;
const MTN_YELLOW = '#FFCC00';
const MTN_YELLOW_DISABLED = 'rgba(255,204,0,0.4)';

type Step = 'msisdn' | 'otp' | 'success';

const OTP_INVALID_MESSAGES: Record<string, string> = {
  invalid: 'The OTP is incorrect. Please try again.',
  expired: 'The OTP has expired. Please request a new one.',
  used: 'This OTP was already used. Please request a new one.',
};

interface MtnOtpLoginProps {
  onSuccess?: () => void;
  onBack?: () => void;
  compact?: boolean;
}

/** Normalise a Ghana number to 233XXXXXXXXX (backend does the same check). */
export const formatGhanaMsisdn = (raw: string): string => {
  const digits = raw.replace(/\D/g, '');
  if (digits.startsWith('00233')) return digits.slice(2);
  if (digits.startsWith('233')) return digits;
  if (digits.startsWith('0')) return `233${digits.slice(1)}`;
  return `233${digits}`;
};

const getDeviceInfo = (): IDeviceInfo => ({
  name: navigator.userAgent,
  platform: navigator.platform,
  browser: navigator.appName || 'Unknown',
  userAgent: navigator.userAgent,
});

const MtnOtpLogin: FC<MtnOtpLoginProps> = ({onSuccess, onBack, compact}) => {
  const [step, setStep] = useState<Step>('msisdn');
  const [msisdn, setMsisdn] = useState('');
  const [otp, setOtp] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);
  const [isCompleting, setIsCompleting] = useState(false);
  const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [requestOtp, {isLoading: isRequestingOtp}] = useMtnRequestOtpMutation();
  const [verifyOtp, {isLoading: isVerifyingOtp}] = useMtnVerifyOtpMutation();
  const completeLogin = useMtnLoginCompletion();
  const dispatch = useAppDispatch();
  const {t} = useTranslation('auth');

  useEffect(() => {
    return () => {
      if (cooldownRef.current) clearInterval(cooldownRef.current);
    };
  }, []);

  const startCooldown = useCallback(() => {
    setCooldown(OTP_COOLDOWN_MS / 1000);
    if (cooldownRef.current) clearInterval(cooldownRef.current);
    cooldownRef.current = setInterval(() => {
      setCooldown(prev => {
        if (prev <= 1) {
          if (cooldownRef.current) clearInterval(cooldownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  const finishLogin = useCallback(
    async (response: MtnAllowLoginResponse) => {
      setIsCompleting(true);
      try {
        const mode = await completeLogin(response);
        setOtp('');
        setCooldown(0);
        if (cooldownRef.current) clearInterval(cooldownRef.current);

        // Test login is already in the store and the modal is closed
        if (mode === 'test') {
          onSuccess?.();
          return;
        }

        setStep('success');
        setTimeout(() => {
          dispatch(appAuthSlice.actions.closeAuthModal());
          onSuccess?.();
        }, 2000);
      } finally {
        setIsCompleting(false);
      }
    },
    [completeLogin, dispatch, onSuccess],
  );

  const handleRequestOtp = useCallback(async () => {
    setError(null);
    const formatted = formatGhanaMsisdn(msisdn);
    if (!/^233\d{9}$/.test(formatted)) {
      setError(t('Please enter a valid MTN Ghana phone number'));
      return;
    }

    try {
      const response = await requestOtp({msisdn: formatted}).unwrap();
      switch (response.action) {
        case 'otp_sent':
          setStep('otp');
          startCooldown();
          break;
        case 'redirect_to_penrose':
          // Not subscribed (PENDING / CANCELLED): straight to the MTN subscribe page
          window.location.href = response.url || MTN_SUBSCRIBE_URL;
          break;
        case 'otp_send_failed':
          setError(t('We could not send the OTP. Please try again later.'));
          break;
        case 'allow_login':
          // Backend testing mode MTN_LOGIN_WITHOUT_OTP: logged in straight away
          await finishLogin(response);
          break;
      }
    } catch (err: any) {
      setError(
        err?.data?.message || t('Failed to send OTP. Please try again.'),
      );
    }
  }, [msisdn, requestOtp, startCooldown, finishLogin, t]);

  const handleResendOtp = useCallback(async () => {
    if (cooldown > 0) return;
    setError(null);
    try {
      const response = await requestOtp({
        msisdn: formatGhanaMsisdn(msisdn),
      }).unwrap();
      if (response.action === 'otp_sent') {
        startCooldown();
      } else {
        setError(t('Failed to resend OTP.'));
      }
    } catch (err: any) {
      setError(err?.data?.message || t('Failed to resend OTP.'));
    }
  }, [msisdn, cooldown, requestOtp, startCooldown, t]);

  const handleVerifyOtp = useCallback(async () => {
    setError(null);
    if (otp.length < 4) {
      setError(t('Please enter the OTP received via SMS'));
      return;
    }

    try {
      const response = await verifyOtp({
        msisdn: formatGhanaMsisdn(msisdn),
        otp,
        deviceInfo: getDeviceInfo(),
      }).unwrap();

      if (response.action === 'otp_invalid') {
        setError(
          t(OTP_INVALID_MESSAGES[response.reason] || OTP_INVALID_MESSAGES.invalid),
        );
        return;
      }
      await finishLogin(response);
    } catch (err: any) {
      setError(
        err?.data?.message || t('OTP verification failed. Please try again.'),
      );
    }
  }, [otp, msisdn, verifyOtp, finishLogin, t]);

  const isLoading = isRequestingOtp || isVerifyingOtp || isCompleting;

  const inputBox = {
    width: '100%' as any,
    maxWidth: 360,
    backgroundColor: 'rgba(255,255,255,0.08)',
    borderRadius: 8,
    paddingHorizontal: Metrics.horizontalScale(12),
    height: Metrics.verticalScale(48),
    marginBottom: Metrics.verticalScale(12),
    opacity: isLoading ? 0.5 : 1,
  };

  const renderButton = (
    label: string,
    onPress: () => void,
    opts: {disabled?: boolean; loading?: boolean; loadingLabel?: string} = {},
  ) => (
    <Pressable
      onPress={onPress}
      disabled={opts.disabled}
      style={{
        backgroundColor: opts.disabled ? MTN_YELLOW_DISABLED : MTN_YELLOW,
        width: '100%' as any,
        maxWidth: 360,
        paddingVertical: Metrics.verticalScale(14),
        borderRadius: 8,
        alignItems: 'center',
        marginBottom: Metrics.verticalScale(12),
      }}>
      {opts.loading ? (
        <View style={{flexDirection: 'row', alignItems: 'center'}}>
          <ActivityIndicator
            size="small"
            color="#000"
            style={{marginRight: Metrics.horizontalScale(8)}}
          />
          <Typography variant="button1" style={{color: '#000'}}>
            {opts.loadingLabel || label}
          </Typography>
        </View>
      ) : (
        <Typography variant="button1" style={{color: '#000'}}>
          {label}
        </Typography>
      )}
    </Pressable>
  );

  const hint = (text: string) => (
    <Typography
      variant="body2"
      color="gray"
      style={{
        textAlign: 'center',
        marginBottom: Metrics.verticalScale(16),
        maxWidth: 320,
      }}>
      {text}
    </Typography>
  );

  const displayNumber = `+${formatGhanaMsisdn(msisdn)}`;

  return (
    <View
      style={{
        width: '100%' as any,
        alignItems: 'center',
        padding: compact ? 0 : Metrics.horizontalScale(16),
      }}>
      {onBack && step === 'msisdn' && (
        <Pressable
          onPress={onBack}
          style={{
            alignSelf: 'flex-start',
            marginBottom: Metrics.verticalScale(8),
          }}>
          <Typography variant="body2" color="primary">
            ← {t('Back')}
          </Typography>
        </Pressable>
      )}

      <Icon
        name="mtn"
        height={step === 'success' ? 120 : 72}
        style={{marginBottom: Metrics.verticalScale(16)}}
      />

      {step === 'msisdn' && (
        <>
          {hint(t('Enter your MTN mobile number to receive a one-time PIN'))}
          <View
            style={{...inputBox, flexDirection: 'row', alignItems: 'center'}}>
            <Typography
              variant="body1"
              color="gray"
              style={{marginRight: Metrics.horizontalScale(8)}}>
              +233
            </Typography>
            <TextInput
              value={msisdn}
              onChangeText={setMsisdn}
              placeholder={t('Phone number') as string}
              placeholderTextColor="rgba(255,255,255,0.3)"
              keyboardType="phone-pad"
              maxLength={15}
              editable={!isLoading}
              style={
                {flex: 1, color: '#fff', fontSize: 16, outlineStyle: 'none'} as any
              }
              onSubmitEditing={handleRequestOtp}
            />
          </View>
          {renderButton(t('Send OTP'), handleRequestOtp, {
            disabled: isLoading || !msisdn.trim(),
            loading: isRequestingOtp || isCompleting,
            loadingLabel: t('Sending...'),
          })}
        </>
      )}

      {step === 'otp' && (
        <>
          {hint(`${t('Enter the OTP sent to')} ${displayNumber}`)}
          <View style={{...inputBox, justifyContent: 'center'}}>
            <TextInput
              value={otp}
              onChangeText={setOtp}
              placeholder={t('Enter OTP') as string}
              placeholderTextColor="rgba(255,255,255,0.3)"
              keyboardType="number-pad"
              maxLength={6}
              editable={!isLoading}
              style={
                {
                  color: '#fff',
                  fontSize: 20,
                  textAlign: 'center',
                  letterSpacing: 8,
                  outlineStyle: 'none',
                } as any
              }
              onSubmitEditing={handleVerifyOtp}
            />
          </View>
          {renderButton(t('Verify'), handleVerifyOtp, {
            disabled: isLoading || otp.length < 4,
            loading: isVerifyingOtp || isCompleting,
            loadingLabel: t('Verifying...'),
          })}
          <Pressable
            onPress={handleResendOtp}
            disabled={cooldown > 0 || isLoading}>
            <Typography variant="body2" color={cooldown > 0 ? 'gray' : 'primary'}>
              {cooldown > 0
                ? t('Resend OTP in {{seconds}}s', {seconds: cooldown})
                : t('Resend OTP')}
            </Typography>
          </Pressable>
          {!isLoading && (
            <Pressable
              onPress={() => {
                setStep('msisdn');
                setOtp('');
                setError(null);
              }}
              style={{marginTop: Metrics.verticalScale(8)}}>
              <Typography variant="body2" color="gray">
                {t('Change number')}
              </Typography>
            </Pressable>
          )}
        </>
      )}

      {step === 'success' && (
        <Typography
          variant="body1"
          color="white"
          style={{
            textAlign: 'center',
            maxWidth: 320,
            paddingVertical: Metrics.verticalScale(24),
          }}>
          {t('Successfully signed in with MTN')}
        </Typography>
      )}

      {error && (
        <Typography
          variant="body2"
          color="error"
          style={{
            textAlign: 'center',
            marginTop: Metrics.verticalScale(12),
            maxWidth: 320,
          }}>
          {error}
        </Typography>
      )}
    </View>
  );
};

export default MtnOtpLogin;
